// Scoped to this form and the exposed content part of pure-web-bottom-sheet 1.0.0.
export function attachSheetKeyboard(form: HTMLFormElement | null) {
  const sheetHost = form?.closest<HTMLElement>('bottom-sheet');
  const visualViewport = window.visualViewport;
  if (!form || !(sheetHost instanceof HTMLElement) || !visualViewport) return;

  const formEl = form;
  const sheet = sheetHost;
  const viewport = visualViewport;

  let frame = 0;
  let touch: { x: number; y: number } | undefined;
  let focusingWithoutScroll = false;

  function focusWithoutScroll(input: HTMLInputElement) {
    focusingWithoutScroll = true;
    input.focus({ preventScroll: true });
    focusingWithoutScroll = false;
  }

  function updateViewport() {
    const input = document.activeElement;
    const focused = input instanceof HTMLInputElement && formEl.contains(input);
    const inset =
      viewport.scale === 1 && focused
        ? Math.max(
            0,
            sheet.getBoundingClientRect().bottom -
              viewport.height -
              viewport.offsetTop
          )
        : 0;
    sheet.style.setProperty('--sheet-keyboard-inset', `${inset}px`);

    if (focused) {
      const content =
        sheet.shadowRoot?.querySelector<HTMLElement>('[part="content"]');
      if (!content) return;
      const bounds = content.getBoundingClientRect();
      const field = input.getBoundingClientRect();
      const bottom =
        Math.min(bounds.bottom, viewport.height + viewport.offsetTop) - 16;
      const top = bounds.top + 16;
      if (field.bottom > bottom) content.scrollTop += field.bottom - bottom;
      else if (field.top < top) content.scrollTop += field.top - top;
    }
  }

  function onFocus(event: FocusEvent) {
    if (!(event.target instanceof HTMLInputElement)) return;
    // Safari can focus the clipped input from a nearby tap without dispatching
    // touch events to the form. Replace that native focus before it pans the page.
    if (!focusingWithoutScroll && navigator.maxTouchPoints > 0) {
      event.target.blur();
      focusWithoutScroll(event.target);
      return;
    }
    cancelAnimationFrame(frame);
    // Scroll only the host: snapToPoint() uses scrollIntoView on all ancestors.
    sheet.scrollTo({ top: sheet.clientHeight, behavior: 'instant' });
    frame = requestAnimationFrame(updateViewport);
  }

  function onBlur(event: FocusEvent) {
    // Safari's keyboard Previous/Next controls otherwise scroll the page.
    if (
      event.relatedTarget instanceof HTMLInputElement &&
      formEl.contains(event.relatedTarget)
    ) {
      focusWithoutScroll(event.relatedTarget);
    }
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(updateViewport);
  }

  function onTouchStart(event: TouchEvent) {
    const point = event.touches[0];
    touch =
      event.touches.length === 1
        ? { x: point.clientX, y: point.clientY }
        : undefined;
  }

  function onTouchMove(event: TouchEvent) {
    const point = event.touches[0];
    if (
      event.touches.length !== 1 ||
      (touch &&
        (Math.abs(point.clientX - touch.x) > 8 ||
          Math.abs(point.clientY - touch.y) > 8))
    ) {
      touch = undefined;
    }
  }

  function onTouchCancel() {
    touch = undefined;
  }

  function onTouchEnd(event: TouchEvent) {
    const { target } = event;
    if (
      touch &&
      target instanceof HTMLInputElement &&
      target !== document.activeElement
    ) {
      event.preventDefault();
      focusWithoutScroll(target);
    }
    touch = undefined;
  }

  formEl.addEventListener('focusin', onFocus);
  formEl.addEventListener('focusout', onBlur);
  formEl.addEventListener('touchstart', onTouchStart, { passive: true });
  formEl.addEventListener('touchmove', onTouchMove, { passive: true });
  formEl.addEventListener('touchend', onTouchEnd, { passive: false });
  formEl.addEventListener('touchcancel', onTouchCancel);
  viewport.addEventListener('resize', updateViewport);
  viewport.addEventListener('scroll', updateViewport);
  return () => {
    cancelAnimationFrame(frame);
    formEl.removeEventListener('focusin', onFocus);
    formEl.removeEventListener('focusout', onBlur);
    formEl.removeEventListener('touchstart', onTouchStart);
    formEl.removeEventListener('touchmove', onTouchMove);
    formEl.removeEventListener('touchend', onTouchEnd);
    formEl.removeEventListener('touchcancel', onTouchCancel);
    viewport.removeEventListener('resize', updateViewport);
    viewport.removeEventListener('scroll', updateViewport);
    sheet.style.removeProperty('--sheet-keyboard-inset');
  };
}
